import React, { useState, useEffect } from "react";
import {
  Container, Paper, styled, Typography, TextField, Modal, Button, Box, FormControl, InputLabel, Select, MenuItem, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Dialog, Tooltip, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import base64 from "base64-js";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


function ExamSubCategory() {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const { encodedFormId } = useParams();
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [className, setClassName] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [subCategory, setSubCategory] = useState([]);
  const [selectedClassTypeId, setSelectedClassTypeId] = useState("");
  const [selectedExamCategoryId, setSelectedExamCategoryId] = useState("");
  const [isWeightageDisabled, setIsWeightageDisabled] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [subCategoryIdToDelete, setSubCategoryIdToDelete] = useState(null);
  const [errors, setErrors] = useState({ minMarks: '', maxMarks: '' });
  const [formData, setFormData] = useState({ subExam: "", minMarks: 0, maxMarks: 0, weightage: 0 });
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");


  const navigate = useNavigate();

  const decodeFormId = (encodedFormId) => {
    try {
      const bytes = base64.toByteArray(encodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return "";
    }
  };

  const formId = decodeFormId(encodedFormId);

  const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 20,
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  }));

  const fetchClassddl = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ClassType/GetClassType`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const responseData = await response.json();
        if (responseData.data !== null) {
          setClassName(responseData);
        } else {
          console.error('No data found for classes');
        }
      } else {
        console.error('Failed to fetch class data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId }),
      });
      if (response.ok) {
        const responseData = await response.json();
        const authorizationData = responseData[0];
        setCanDelete(authorizationData.uDelete === 1);
        setCanEdit(authorizationData.uModify === 1);
        setCanSave(authorizationData.uWrite === 1);
      } else {
        console.error("Authorization failed:", response.status);
        // alert("Invalid authorization response");
        setSnackbarMessage('Invalid authorization response');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    }
  };

  const fetchExamTypes = async (classTypeId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ExaminationSubCategory/Examddl_subcategory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classTypeId }),
      });
      if (response.ok) {
        const responseData = await response.json();
        if (responseData.data !== null) {
          const filteredExamTypes = responseData.filter(item => item.isExamModeIndependent === 0);
          setExamTypes(filteredExamTypes);
        } else {
          console.error('No data found for exam types');
        }
      } else {
        console.error('Failed to fetch exam types data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const handleClassTypeChange = (event) => {
    const classTypeId = event.target.value;
    setSelectedClassTypeId(classTypeId);
    fetchExamTypes(classTypeId);
  };

  const handleExamTypeChange = (event) => {
    const examCriteriaId = event.target.value;
    setSelectedExamCategoryId(examCriteriaId);
    const selectedExamType = examTypes.find(item => item.examCriteriaId === examCriteriaId);
    if (selectedExamType) {
      setIsWeightageDisabled(selectedExamType.isExamWeightIndependent === 1);
    }
    fetchExamSubCategory(examCriteriaId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (['minMarks', 'maxMarks', 'weightage'].includes(name)) {
      newValue = value === '' ? '' : Math.max(1, Math.min(100, Number(value)));
    }
    setFormData((prevData) => ({ ...prevData, [name]: newValue }));
    if (name === 'minMarks' || name === 'maxMarks') {
      validateMarks(name, newValue);
    }
  };

  const validateMarks = (field, value) => {
    let newErrors = { ...errors };
    const minMarks = field === 'minMarks' ? Number(value) : Number(formData.minMarks);
    const maxMarks = field === 'maxMarks' ? Number(value) : Number(formData.maxMarks);
    if (minMarks < 1) newErrors.minMarks = 'Min Marks must be at least 1';
    else newErrors.minMarks = '';
    if (maxMarks > 100) newErrors.maxMarks = 'Max Marks cannot exceed 100';
    else if (maxMarks <= minMarks) newErrors.maxMarks = 'Max Marks must be greater than Min Marks';
    else newErrors.maxMarks = '';
    setErrors(newErrors);
  };

  const totalWeightage = Array.isArray(subCategory) ? subCategory.reduce((sum, item) => sum + Number(item.weightage), 0) : 0;
  const isWeightageValid = (totalWeightage + Number(formData.weightage)) <= 100;

  const handleSave = async () => {
    if (!canSave) {
      // alert("You don’t have permission to save this subcategory.");
      setSnackbarMessage('You don’t have permission to save this subcategory.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
      return;
    }

    if (errors.minMarks || errors.maxMarks || !formData.minMarks || !formData.maxMarks || !isWeightageValid) {
      // alert("Please correct the errors or ensure total weightage does not exceed 100 before saving.");
      setSnackbarMessage('Please correct the errors or ensure total weightage does not exceed 100 before saving.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning")
      return;
    }

    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/ExaminationSubCategory`;
      const token = sessionStorage.getItem('token');
      const requestData = {
        examCategoryId: selectedExamCategoryId || 2,
        classTypeId: selectedClassTypeId,
        subExam: formData.subExam,
        minMarks: formData.minMarks,
        maxMarks: formData.maxMarks,
        weightage: formData.weightage || 0
      };
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(requestData),
      });
      if (response.ok) {
        // alert('Data saved successfully');
        setSnackbarMessage('Data saved successfully');
        setOpenSnackbar(true);
        setSnackbarSeverity("success")
        setFormData({ subExam: "", minMarks: 1, maxMarks: 1, weightage: 1 });
        fetchExamSubCategory(selectedExamCategoryId);
      } else {
        console.error('Failed to save data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const handleCancel = () => {
    setFormData({ subExam: "", minMarks: 0, maxMarks: 0, weightage: 0 });
  };

  const handleEdit = (item) => {
    if (!canEdit) {
      // alert("You don’t have permission to edit this subcategory.");
      setSnackbarMessage('You don’t have permission to edit this subcategory.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    navigate(`/updateexamsubcategory/${item.subCategoryId}`, { state: { subCategoryData: item } });
  };

  const handleDeleteClick = (subCategoryId) => {
    if (!canDelete) {
      // alert("You don’t have permission to delete this subcategory.");
       setSnackbarMessage('You don’t have permission to delete this subcategory.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    setSubCategoryIdToDelete(subCategoryId);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/ExaminationSubCategory/Id?Id=${subCategoryIdToDelete}`;
      const token = sessionStorage.getItem('token');
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });
      if (response.ok) {
        // alert('SubCategory deleted successfully');
        setSnackbarMessage('SubCategory deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity("success")
        setOpenDialog(false);
        fetchExamSubCategory(selectedExamCategoryId);
      } else {
        console.error('Delete failed');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
  };

  const fetchExamSubCategory = async (examCriteriaId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ExaminationSubCategory/GetExamBySubCategory`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          examCategoryId: examCriteriaId,
          classTypeId: selectedClassTypeId,
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        if (responseData.data !== null) {
          setSubCategory(Array.isArray(responseData) ? responseData : []);
        } else {
          setSubCategory([]);
        }
      } else {
        console.error('Failed to fetch subcategory data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  useEffect(() => {
    fetchClassddl();
    Authorizer();
  }, []);

  return (
    <Container maxWidth="lg" style={{ marginTop: "40px" }}>
      <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 24px', boxShadow: 3, borderRadius: '4px 4px 0 0', marginBottom: '12px', width: "100%" }}>
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold' }}>
          Exam Sub-Category <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6" }} />
        </Typography>
      </Box>
      <Dialog open={showUserGuide} onClose={() => setShowUserGuide(false)} maxWidth="md" fullWidth sx={{ "& .MuiDialog-paper": { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", backgroundColor: "#f9fafb" } }}>
        <DialogTitle sx={{ background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)", color: "white", fontWeight: "bold", textAlign: "center", borderRadius: "12px 12px 0 0", py: 2 }}>
          Exam Sub-Category User Guide
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="h6" sx={{ color: "#1898c2", fontWeight: "600", mb: 2, mt: 1, textAlign: "center" }}>
            Quick Steps to Add Exam Sub-Category
          </Typography>
          <Box component="ul" sx={{ listStyleType: "none", padding: 0, "& li": { display: "flex", alignItems: "flex-start", mb: 1.5, fontSize: "1.0rem", color: "#333" }, "& li::before": { content: '"\\2022"', color: "#1898c2", fontWeight: "bold", display: "inline-block", width: "1.5em", mr: 1, fontSize: "1.2rem" }, "& strong": { margin: "0 0.2em" }, "& em": { margin: "0 0.2em" } }}>
            <li>Select a <strong>Class Type</strong> from the dropdown menu.</li>
            <li>Choose an <strong>Exam Type</strong> from the dropdown menu.</li>
            <li>Enter the <strong>Sub Exam</strong> name in the text field.</li>
            <li>Specify the <strong>Min Marks</strong> and <strong>Max Marks</strong> (values between 1 and 100).</li>
            <li>Set the <strong>Weightage</strong> for the sub-exam, ensuring the total weightage does not exceed 100.</li>
            <li>Click the <strong>Save</strong> button to store the sub-exam details.</li>
            <li>To cancel the current operation, click the <strong>Cancel</strong> button.</li>
            <li>View the list of sub-exams in the table below the form.</li>
            <li>To edit a sub-exam, click the <strong>Edit</strong> button next to the sub-exam entry in the table.</li>
            <li>To delete a sub-exam, click the <strong>Delete</strong> button next to the sub-exam entry in the table and confirm the deletion.</li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <StyledButton onClick={() => setShowUserGuide(false)} color="primary" variant="contained" sx={{ backgroundColor: "#1898c2", "&:hover": { backgroundColor: "#156f9a" }, borderRadius: "8px", px: 4 }}>
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>
      <Paper elevation={3} style={{ padding: "20px", textAlign: "center" }}>
        <FormControl fullWidth style={{ marginBottom: "20px" }}>
          <InputLabel htmlFor="classType">Class Type</InputLabel>
          <Select id="classType" label="Class Type" value={selectedClassTypeId} onChange={handleClassTypeChange}>
            <MenuItem value=""><em>--Select--</em></MenuItem>
            {className.map((item) => (
              <MenuItem key={item.classTypeId} value={item.classTypeId}>{item.classType}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth style={{ marginBottom: "20px" }}>
          <InputLabel htmlFor="examType">Exam Type</InputLabel>
          <Select id="examType" label="Exam Type" defaultValue="" value={selectedExamCategoryId} onChange={handleExamTypeChange}>
            <MenuItem value=""><em>--Select--</em></MenuItem>
            {examTypes.map((item) => (
              <MenuItem key={item.examCriteriaId} value={item.examCriteriaId}>{item.examType}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField fullWidth label="Sub Exam" value={formData.subExam} onChange={(e) => setFormData({ ...formData, subExam: e.target.value })} style={{ marginBottom: "20px" }} />
        <TextField fullWidth label="Min Marks" type="number" name="minMarks" value={formData.minMarks} onChange={handleInputChange} error={!!errors.minMarks} helperText={errors.minMarks} style={{ marginBottom: "20px" }} />
        <TextField fullWidth label="Max Marks" type="number" name="maxMarks" value={formData.maxMarks} onChange={handleInputChange} error={!!errors.maxMarks} helperText={errors.maxMarks} style={{ marginBottom: "20px" }} />
        <Tooltip title={`Weightage cannot exceed ${100 - totalWeightage} to keep total at or below 100`} placement="bottom">
          <TextField fullWidth label="Weightage" type="number" name="weightage" value={formData.weightage} onChange={handleInputChange} style={{ marginBottom: "20px" }} disabled={isWeightageDisabled} error={!isWeightageValid} helperText={!isWeightageValid ? `Weightage cannot exceed ${100 - totalWeightage}` : ''} />
        </Tooltip>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Typography variant="subtitle1">
            Weightage: {totalWeightage} out of 100
          </Typography>
          <div>
            <Tooltip title={canSave ? "Save SubCategory" : "Not authorized to save"} arrow>
              <span>
                <Button variant="contained" color="success" style={{ marginRight: "6px" }} onClick={handleSave} disabled={!canSave || !isWeightageValid || errors.minMarks || errors.maxMarks || !formData.minMarks || !formData.maxMarks}>
                  <b>Save</b>
                </Button>
              </span>
            </Tooltip>
            <Button variant="contained" color="warning" onClick={handleCancel}>
              <b>Cancel</b>
            </Button>
          </div>
        </div>
        <TableContainer component={Paper} style={{ marginTop: "20px" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Sub Exam</strong></TableCell>
                <TableCell><strong>Class Type</strong></TableCell>
                <TableCell><strong>Exam Type</strong></TableCell>
                <TableCell><strong>Min Marks</strong></TableCell>
                <TableCell><strong>Max Marks</strong></TableCell>
                <TableCell><strong>Weightage</strong></TableCell>
                <TableCell style={{ textAlign: "center" }}><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(subCategory) && subCategory.length > 0 ? (
                subCategory.map((item) => (
                  <TableRow key={item.subCategoryId}>
                    <TableCell>{item.subExam}</TableCell>
                    <TableCell>{item.classType}</TableCell>
                    <TableCell>{item.examType}</TableCell>
                    <TableCell>{item.minMarks}</TableCell>
                    <TableCell>{item.maxMarks}</TableCell>
                    <TableCell>{item.weightage}</TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      <Tooltip title={canEdit ? "Edit SubCategory" : "Not authorized to edit"} arrow>
                        <span>
                          {/* <Button className="mx-2" variant="contained" color="warning" onClick={() => handleEdit(item)} disabled={!canEdit}>
                            Edit
                          </Button> */}
                        </span>
                      </Tooltip>
                      <Tooltip title={canDelete ? "Delete SubCategory" : "Not authorized to delete"} arrow>
                        <span>
                          <Button variant="contained" color="error" onClick={() => handleDeleteClick(item.subCategoryId)} disabled={!canDelete}>
                            Delete
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">No data available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Modal
        open={openDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-subcategory-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden", // Ensures rounded corners are respected
          }}
        >
          {/* Header: Dark Navy Blue */}
          <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 24px" }}>
            <Typography
              id="delete-subcategory-title"
              variant="h6"
              sx={{ color: "#fff", fontWeight: "bold" }}
            >
              Confirm Deletion ⚠️
            </Typography>
          </Box>

          {/* Content: Confirmation Message */}
          <Box sx={{ padding: "20px", textAlign: "center" }}>
            <Typography variant="body1">
              Are you sure you want to delete this subcategory?
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "16px 24px", gap: "10px" }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
            >
              Yes, Delete
            </Button>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>


    </Container>
  );
}

export default ExamSubCategory;
